import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const codeExamples = [
    {
      language: 'React',
      code: `const TransformBusiness = () => {
  const [growth, setGrowth] = useState(0);
  
  useEffect(() => {
    // Digital transformation magic
    const interval = setInterval(() => {
      setGrowth(prev => prev + 15);
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="transformation-complete">
      <h1>Revenue Growth: {growth}%</h1>
    </div>
  );
};`
    },
    {
      language: 'Node.js',
      code: `const express = require('express');
const app = express();

// Scalable API architecture
app.use('/api/v1', (req, res) => {
  const transformation = {
    performance: 'optimized',
    scalability: 'enterprise-ready',
    security: 'bank-grade'
  };
  
  res.json({
    status: 'success',
    data: transformation
  });
});

app.listen(3000, () => {
  console.log('Digital transformation live!');
});`
    },
    {
      language: 'Python',
      code: `import asyncio
from fastapi import FastAPI

app = FastAPI(title="AI-Powered Solutions")

@app.get("/transform/{business_model}")
async def transform_business(business_model: str):
    # Machine learning optimization
    insights = await analyze_market_data()
    
    return {
        "transformation_plan": insights,
        "roi_projection": "300% in 12 months",
        "implementation": "seamless"
    }

# Where innovation meets execution`
    }
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentLanguage((prev) => (prev + 1) % codeExamples.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-muted/30 to-secondary/5">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-32 h-32 bg-secondary/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-primary/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-accent/10 rounded-full blur-lg animate-pulse delay-500"></div>
        
        {/* Geometric Shapes */}
        <div className="absolute top-1/4 right-1/4 transform rotate-45">
          <div className="w-16 h-16 border-2 border-secondary/20 animate-spin-slow"></div>
        </div>
        <div className="absolute bottom-1/3 left-1/3 transform -rotate-12">
          <div className="w-12 h-12 bg-primary/20 transform rotate-45 animate-pulse"></div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-medium">
                <Icon name="Sparkles" size={16} />
                <span>Digital Transformation Architects</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                We don't just build{' '}
                <span className="text-gradient-brand">software</span>,{' '}
                <br />
                we architect{' '}
                <span className="text-gradient-brand">digital transformation</span>
              </h1>
              
              <p className="text-xl text-text-secondary leading-relaxed max-w-2xl">
                Transform your business vision into powerful digital solutions that drive growth, 
                enhance efficiency, and create lasting competitive advantages through expert software craftsmanship.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/about-ecosystem">
                <Button
                  variant="default"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="cta-primary w-full sm:w-auto"
                >
                  Explore Our Transformations
                </Button>
              </Link>
              
              <Link to="/contact-consultation">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                  className="w-full sm:w-auto"
                >
                  Start Your Project
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-6 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={20} className="text-success" />
                <span className="text-sm text-text-secondary">Enterprise Security</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Zap" size={20} className="text-warning" />
                <span className="text-sm text-text-secondary">Rapid Deployment</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={20} className="text-secondary" />
                <span className="text-sm text-text-secondary">99.9% Uptime</span>
              </div>
            </div>
          </div>

          {/* Right Content - Code Visualization */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="relative bg-card border border-border rounded-brand shadow-brand-card overflow-hidden">
              {/* Code Editor Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-muted border-b border-border">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-text-secondary ml-4">
                    {codeExamples[currentLanguage].language} - transformation.{codeExamples[currentLanguage].language.toLowerCase()}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Play" size={16} className="text-success" />
                  <span className="text-xs text-success">Running</span>
                </div>
              </div>

              {/* Code Content */}
              <div className="p-6 bg-gray-900 text-green-400 font-mono text-sm overflow-hidden">
                <pre className="whitespace-pre-wrap transition-all duration-500">
                  {codeExamples[currentLanguage].code}
                </pre>
              </div>

              {/* Language Indicators */}
              <div className="absolute bottom-4 right-4 flex space-x-2">
                {codeExamples.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentLanguage ? 'bg-secondary' : 'bg-border'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Floating Stats */}
            <div className="absolute -top-4 -right-4 bg-success text-white px-4 py-2 rounded-brand shadow-lg">
              <div className="flex items-center space-x-2">
                <Icon name="TrendingUp" size={16} />
                <span className="text-sm font-medium">300% ROI Average</span>
              </div>
            </div>

            <div className="absolute -bottom-4 -left-4 bg-secondary text-white px-4 py-2 rounded-brand shadow-lg">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span className="text-sm font-medium">50% Faster Delivery</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={24} className="text-text-secondary" />
      </div>
    </section>
  );
};

export default HeroSection;